import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReadCard from '../landing/astraLite/ReadCard';
import {
    DEFAULT_GRADE, GRADES, STAND, closesLine, gpaOf, homeOptions, isEarlyGrade, nextDeadline, pictureText, residencyLine,
    schoolState,
} from '../../content/collegeStand';
import { astraPicture, astraRead, astraRefine, pageTimeForAsk } from '../../services/astraLite';
import { holdReadForSignIn, markStandSave } from '../../services/astraCarry';
import { track, EVENTS } from '../../services/analytics';
import useShownOnce from '../../hooks/useShownOnce';
import MomentBox from '../signup/MomentBox';
import { COPY as MOMENT_COPY, MOMENTS, admitsPhrase, targetsBucket } from '../../content/signupMoments';
import { holdMoment, momentPayoff, noteCollegeRead } from '../../services/signupMoments';

// the read's band, as an event prop only from this closed list
const BANDS = ['Safety', 'Target', 'Reach'];

// "Where do I stand at <School>?" (#3885; founder pick A of the college-page
// sign-up mocks, 2026-10-11). Under the fact tiles: grade, unweighted GPA and
// home state, then Astra Lite's existing no-sign-in read in place (the
// landing page's picture -> read path, its caps and budget unchanged; no
// route of its own). Then one action: save it, which hands the read over the
// way the landing page's unlock box does (#3480: the server re-checks the
// session token at the carry, then puts the school on the new account's
// list, where the weekly email names its deadlines) and opens Google.
//
// Shown only to a visitor who is not signed in, where the open chat answers
// (`available`, the Ask box's own rule). `onSignIn(entry)`: the page's Google
// sign-in. `onUnavailable()`: the route said it is closed or its budget is
// spent; the page hides the card (and the box) rather than show an error.
//
// Nothing about a student is prerendered: the read appears only after the
// visitor asks for it, from the server, and never rides an analytics event
// (grade and an in-state boolean do; the GPA and the state never).
//
// `landing` (#4476): the paid first screen's landing when the visitor came
// from an ad; every event then carries landing_layout and landing_answer, so
// the card's views and checks read per landing and per answer.
//
// `moments` (#5195, signup_moments, founder pick A): under the read, the
// fixed-band line when the band holds at every GPA, then the sign-up
// moment's box (the Targets count and what a free account adds) in place of
// the save button; its button opens the sheet with its own words and lands
// on /start with the school offered. `via`: 'long_read' when the visitor
// came from the block halfway down the page, so the box counts as that
// moment. `onChecked()`: a read landed (the long-read block stops waiting).
// Off: this card exactly as before.
export default function StandCard({ college, available = true, onSignIn, onUnavailable, landing = null, className = '',
    moments = false, via = null, onChecked = null }) {
    const short = college.shortName || college.name;
    const ids = { slug: college.slug, school_id: college.kbId,
        ...(landing ? { landing_layout: landing.layout, landing_answer: landing.answer } : {}) };
    const options = useMemo(() => homeOptions(college), [college]);
    const own = schoolState(college);
    const [grade, setGrade] = useState(DEFAULT_GRADE);
    const [gpaDraft, setGpaDraft] = useState('');
    const [home, setHome] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);   // {token, fields, handoff, text, read, home, early, next, gpa, why}
    // the moment's free taste for this result: undefined while asked, null when none
    const [payoff, setPayoff] = useState(undefined);
    const ref = useRef(null);
    // a second submit in the same tick, before `busy` re-renders, is dropped
    const inFlight = useRef(false);
    useShownOnce(ref, available, () => track(EVENTS.COLLEGE_STAND_SHOWN, ids));

    const moment = via === 'long_read' ? 'long_read' : 'chances_result';
    // Ask the server for the moment's taste once a read is on screen.
    useEffect(() => {
        if (!moments || !result) return undefined;
        let alive = true;
        momentPayoff(result.fields, [result.read.university_id || college.kbId], MOMENTS[moment].payoffs)
            .then((p) => { if (alive) setPayoff(p); });
        return () => { alive = false; };
    }, [moments, result]);   // eslint-disable-line react-hooks/exhaustive-deps

    const band = result && BANDS.includes(result.read.band) ? result.read.band : null;
    const box = moments && result && payoff !== undefined ? MOMENTS[moment].box({
        school: short, band, gpa: result.gpa, targets: payoff ? payoff.targets : null, why: result.why,
        date: result.next ? result.next.short : null, early: result.early, saved: true, thread: false,
    }) : null;
    const fixed = moments && payoff && band && payoff.fixed_band === band
        ? MOMENT_COPY.fixedBand(short, band, admitsPhrase(result.read.band_line)) : null;
    useEffect(() => {
        if (moments && result && payoff !== undefined) {
            noteCollegeRead({ slug: college.slug, band, gpa: result.gpa,
                targetsN: payoff && payoff.targets ? payoff.targets.n : null });
        }
    }, [moments, result, payoff]);   // eslint-disable-line react-hooks/exhaustive-deps

    // The moment's button: the same hand-over as Save (the read, the picture,
    // the school offered on /start), plus the moment record, then the sheet
    // with the button's own words.
    const go = () => {
        if (!result || !box) return;
        holdReadForSignIn({
            token: result.token, fields: result.fields, schoolId: result.read.university_id || college.kbId,
            text: result.text, handoff: result.handoff, reads: [result.read],
            entry: { id: college.kbId, name: college.name, from: 'college_page' },
        });
        markStandSave({ slug: college.slug, schoolId: result.read.university_id || college.kbId });
        holdMoment({ moment, schoolIds: [college.kbId], gpa: result.gpa, state: result.home,
            targets: Boolean(payoff && payoff.targets && payoff.targets.n > 0) });
        onSignIn?.(MOMENTS[moment].entry, { title: box.button, lead: box.lead });
    };
    const momentProps = {
        band, fixed_band: Boolean(fixed), schools_n: 1,
        ...(payoff && payoff.targets ? { targets_n_bucket: targetsBucket(payoff.targets.n) } : {}),
    };

    if (!available) return null;

    const fail = (reason, message) => {
        track(EVENTS.COLLEGE_STAND_CHECK, { state: 'failed', ...ids, grade, in_state: home === own, reason });
        setError(message || STAND.error);
        setBusy(false);
        inFlight.current = false;
    };

    const check = async (e) => {
        e.preventDefault();
        if (inFlight.current) return;
        const gpa = gpaOf(gpaDraft);
        if (gpa === null) { setError(STAND.gpaBad); return; }
        if (!home) { setError(STAND.homeMissing); return; }
        inFlight.current = true;
        setBusy(true);
        setError(null);
        const inState = home === own;
        track(EVENTS.COLLEGE_STAND_CHECK, { state: 'requested', ...ids, grade, in_state: inState });
        const text = pictureText({ grade, gpa, home, schoolName: college.name });
        const pic = await astraPicture(text, await pageTimeForAsk(), '', null, 'college_page');
        if (!pic.success || pic.crisis || pic.under_13) {
            if (pic.busy || pic.status === 404) {
                track(EVENTS.COLLEGE_STAND_CHECK, { state: 'failed', ...ids, grade, in_state: inState, reason: 'unavailable' });
                setBusy(false);
                inFlight.current = false;
                onUnavailable?.();
                return;
            }
            fail(pic.limited ? 'limited' : 'error', pic.success ? null : pic.error);
            return;
        }
        let { token } = pic;
        let fields = pic.fields || {};
        // The parse reads the GPA from the sentence; if it ever misses it,
        // the GPA ask the landing page uses fills it in (op refine).
        if ((pic.missing || []).includes('gpa')) {
            const r = await astraRefine(token, fields, gpa);
            if (!r.success) { fail('error', r.error); return; }
            token = r.token;
            fields = r.fields || { ...fields, gpa_unweighted: gpa };
        }
        const rd = await astraRead(token, fields, college.kbId);
        if (!rd.success || !rd.read) { fail(rd.limited ? 'limited' : 'error', rd.success ? null : rd.error); return; }
        // the grade the read was asked for, not whatever the select says later
        const early = isEarlyGrade(grade);
        setResult({ token: rd.token, fields, handoff: pic.handoff || null, text, read: rd.read, home, early,
            next: early ? null : nextDeadline(college.deadlines), gpa: String(gpa),
            // the server's locked read holds reasons (#3480): only then is
            // "Why <school> is a <band>" an item, never the reasons themselves
            why: Boolean(rd.locked && Array.isArray(rd.locked.items) && rd.locked.items.length) });
        setPayoff(undefined);
        onChecked?.();
        track(EVENTS.COLLEGE_STAND_CHECK, { state: 'shown', ...ids, grade, in_state: inState,
            band: BANDS.includes(rd.read.band) ? rd.read.band : null });
        setBusy(false);
        inFlight.current = false;
    };

    const save = () => {
        if (!result) return;
        track(EVENTS.COLLEGE_STAND_SAVE, { state: 'clicked', ...ids, dated: Boolean(result.next) });
        holdReadForSignIn({
            token: result.token, fields: result.fields, schoolId: result.read.university_id || college.kbId,
            text: result.text, handoff: result.handoff, reads: [result.read],
            entry: { id: college.kbId, name: college.name, from: 'college_page' },
        });
        markStandSave({ slug: college.slug, schoolId: result.read.university_id || college.kbId });
        onSignIn?.('astra_read_unlock');
    };

    const field = 'min-h-[44px] w-full min-w-0 rounded-lg border border-[#CFE0D2] bg-[#FDFCF7] px-2.5 text-sm text-[#1A2E1F] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B4532F]';
    const residency = result ? residencyLine(college, result.home) : null;
    const closes = result && result.next ? closesLine(result.next) : null;

    return (
        <section ref={ref} data-testid="college-stand" aria-labelledby="college-stand-title"
            className={`${className ? `${className} ` : ''}mt-5 rounded-xl border-2 border-[#1A4D2E] bg-[#F0F7F1] p-3 sm:p-4`}>
            <h2 id="college-stand-title" className="text-base font-semibold leading-snug text-[#1A2E1F]">{STAND.title(short)}</h2>
            <form onSubmit={check} noValidate className="mt-2 space-y-2.5">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <div className="grid gap-1">
                        <label htmlFor="college-stand-grade" className="text-xs text-[#4A4A4A]">{STAND.grade}</label>
                        <select id="college-stand-grade" data-testid="college-stand-grade" value={grade}
                            onChange={(e) => setGrade(Number(e.target.value))} className={field}>
                            {GRADES.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                        </select>
                    </div>
                    <div className="grid gap-1">
                        <label htmlFor="college-stand-gpa" className="text-xs text-[#4A4A4A]">{STAND.gpa}</label>
                        <input id="college-stand-gpa" data-testid="college-stand-gpa" type="text" inputMode="decimal"
                            autoComplete="off" maxLength={4} placeholder={STAND.gpaPlaceholder} value={gpaDraft}
                            onChange={(e) => setGpaDraft(e.target.value)} className={field}
                            aria-invalid={error === STAND.gpaBad ? 'true' : undefined}
                            aria-describedby={error ? 'college-stand-error' : undefined} />
                    </div>
                    <div className="col-span-2 grid gap-1 sm:col-span-1">
                        <label htmlFor="college-stand-home" className="text-xs text-[#4A4A4A]">{STAND.home}</label>
                        <select id="college-stand-home" data-testid="college-stand-home" value={home}
                            onChange={(e) => setHome(e.target.value)} className={field}
                            aria-invalid={error === STAND.homeMissing ? 'true' : undefined}>
                            <option value="">{STAND.homePlaceholder}</option>
                            {options.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
                        </select>
                    </div>
                </div>
                <button type="submit" data-testid="college-stand-go" disabled={busy}
                    className="min-h-[44px] rounded-full bg-[#1A4D2E] px-4 text-sm font-semibold text-[#FDFCF7] disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B4532F] focus-visible:ring-offset-2">
                    {busy ? STAND.checking : STAND.go}
                </button>
                {error && <p id="college-stand-error" role="alert" className="text-sm text-[#B4532F]">{error}</p>}
            </form>
            <div aria-live="polite">
                {result && (
                    <div data-testid="college-stand-result" className="mt-3 space-y-3 border-t border-dashed border-[#CFE0D2] pt-3 text-sm text-[#2C2C2C]">
                        {residency && <p data-testid="college-stand-residency">{residency}</p>}
                        <ReadCard read={result.read} testid="college-stand-read" />
                        {fixed && <p data-testid="college-stand-fixed-band" className="font-semibold text-[#1A2E1F]">{fixed}</p>}
                        {result.early && <p data-testid="college-stand-early">{STAND.early}</p>}
                        {closes && (
                            <p data-testid="college-stand-deadline">
                                {closes.who} {closes.verb} <strong>{closes.when}</strong>.
                            </p>
                        )}
                        {box && (
                            <MomentBox moment={moment} box={box} props={{ ...momentProps }} onGo={go}
                                testid="college-stand-moment" />
                        )}
                        {!(moments && (box || payoff === undefined)) && (
                        <button type="button" data-testid="college-stand-save" onClick={save}
                            className="min-h-[44px] rounded-full border border-[#1A4D2E] bg-transparent px-4 py-2 text-left text-sm font-semibold text-[#1A4D2E] hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B4532F] focus-visible:ring-offset-2">
                            {result.early ? STAND.saveList(short) : STAND.save(result.next ? result.next.short : null)}
                        </button>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
